/**
 * WaitTimeIndicator primitive — label-first wait time display.
 * Primary: composite status (absolute threshold + relative park rank).
 * Secondary: numeric minutes + subtle rank badge.
 * Uses waitStatus() for absolute thresholds and compositeStatus() for park-aware ranking.
 */
import React from 'react';
import { waitStatus, compositeStatus, type WaitStatus } from './waitStatus';

export interface WaitTimeIndicatorProps {
  minutes: number | null;
  attractionId?: string;
  /** Retained for backwards-compat, not used in rendering */
  maxMinutes?: number;
  /** 1-based rank within park (1 = shortest wait); null = no park context */
  parkRank?: number | null;
  /** Total operating attractions in the park */
  parkTotal?: number | null;
  /** Show the raw absolute status (for tooltips / debug) */
  showAbsolute?: boolean;
}

const STATUS_PILL: Record<WaitStatus, { bg: string; text: string; ring: string }> = {
  'Walk On': { bg: 'bg-emerald-50 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-200 dark:ring-emerald-700/60' },
  'Short': { bg: 'bg-teal-50 dark:bg-teal-900/40', text: 'text-teal-700 dark:text-teal-300', ring: 'ring-teal-200 dark:ring-teal-700/60' },
  'Moderate': { bg: 'bg-amber-50 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', ring: 'ring-amber-200 dark:ring-amber-700/60' },
  'Long': { bg: 'bg-red-50 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', ring: 'ring-red-200 dark:ring-red-700/60' },
  'Avoid': { bg: 'bg-red-100 dark:bg-red-900/60', text: 'text-red-800 dark:text-red-200', ring: 'ring-red-300 dark:ring-red-700/80' },
  'Closed': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', ring: 'ring-gray-200 dark:ring-gray-700' },
  'Unknown': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', ring: 'ring-gray-200 dark:ring-gray-700' },
};

const STATUS_PILL_CLOSED = STATUS_PILL['Closed'];

/** Rank badge color matching the status palette */
function rankBadgeColor(status: WaitStatus): string {
  // Closed/Unknown get neutral gray; everything else gets a subtle status-matched color
  if (status === 'Closed' || status === 'Unknown') return 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  if (status === 'Walk On') return 'bg-emerald-100 dark:bg-emerald-800/40 text-emerald-700 dark:text-emerald-300';
  if (status === 'Short') return 'bg-teal-100 dark:bg-teal-800/40 text-teal-700 dark:text-teal-300';
  if (status === 'Moderate') return 'bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300';
  if (status === 'Long') return 'bg-red-100 dark:bg-red-800/40 text-red-700 dark:text-red-300';
  if (status === 'Avoid') return 'bg-red-200 dark:bg-red-800/60 text-red-800 dark:text-red-200';
  return 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
}

export function WaitTimeIndicator({ minutes, attractionId, maxMinutes, parkRank, parkTotal, showAbsolute }: WaitTimeIndicatorProps): React.ReactElement {
  if (minutes === null || minutes === undefined) {
    return (
      <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL_CLOSED.bg} ${STATUS_PILL_CLOSED.text} ring-1 ${STATUS_PILL_CLOSED.ring}`}>
        Closed
      </span>
    );
  }

  const composite = compositeStatus(minutes, attractionId, parkRank ?? null, parkTotal ?? null);
  const absolute = waitStatus(minutes, attractionId);

  const hasRank = parkRank != null && parkTotal != null && parkTotal >= 2;

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[composite].bg} ${STATUS_PILL[composite].text} ring-1 ${STATUS_PILL[composite].ring}`}>
        {composite}
      </span>
      <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{minutes}m</span>
      {/* {hasRank && (
        <span
          className={`inline-flex items-center justify-center w-4.5 h-4.5 rounded-full text-[9px] font-bold leading-none shrink-0 ${rankBadgeColor(composite)}`}
          title={`${parkRank} of ${parkTotal} in park${showAbsolute ? ` (absolute: ${absolute})` : ''}`}
        >
          {parkRank}
        </span>
      )} */}
    </span>
  );
}
